import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import cn from 'classnames';
import { CustomModal, MultiSelectFilterPicker, Page, PageContentHeader, PageEmptyState, PageErrorState, Searchbox, TableList, Typography, useDidUpdate, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { CreateProposalButton } from '~/components/CreateProposalButton/CreateProposalButton';
import LoadingIndicator from '~/components/LoadingIndicator';
import { useRestfulWrapper } from '~/hooks/useRestfulWrapper';
import { useServiceTemplateApi } from '~/hooks/useServiceTemplateApi';
import { useLocaleKeys } from '~/i18n';
import { modalSize } from '~/infra/modals';
import { handlePromise } from '~/infra/utils';
import NoDataIcon from '~/integrations/assets/NoDataIcon';
import { serviceTemplateModel } from '~/models';
import { PracticeManagement } from '~/types/api.model';
import { getPMDetails } from '../pm.utils';
import PMMapServiceField from '../PMMapServiceField/PMMapServiceField';
import { useSerialTaskQueue } from './hooks/useSerialTaskQueue';
import styles from './PMMapServicesModal.module.scss';
export const GetPMMapServicesModalOldTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            PMMapServicesModalTID: {
                Root: 'pm-map-services-modal',
                Content: 'pm-map-services-content',
                NoServices: 'pm-map-services-no-services',
                FetchError: 'pm-map-services-fetch-error',
                Search: 'services-search',
                MappedStatusFilter: 'mapped-status-filter',
                ServiceRow: (rowIndex)=>"service-row-".concat(rowIndex),
                ServiceRowMapping: (rowIndex)=>"service-row-".concat(rowIndex, "-mapping")
            }
        }
    });
function getTableConfig(pmName) {
    const tableConfig = [
        {
            columnDef: {
                width: [
                    100,
                    500
                ]
            },
            headerItem: {
                value: 'Anchor service'
            }
        },
        {
            columnDef: {
                width: 550
            },
            headerItem: {
                value: "".concat(getPMDetails(pmName).name, " ").concat(getPMDetails(pmName).template)
            }
        }
    ];
    return {
        columnDefs: tableConfig.map((config)=>config.columnDef),
        headerItems: tableConfig.map((config)=>config.headerItem)
    };
}
var MappedStatus = /*#__PURE__*/ function(MappedStatus) {
    MappedStatus["Mapped"] = "mapped";
    MappedStatus["Unmapped"] = "unmapped";
    return MappedStatus;
}(MappedStatus || {});
function filterTemplates(templates, search, mappedStatusFilter, getServiceTemplateIntegratedIds) {
    const trimmedLowercaseSearch = search.trim().toLowerCase();
    return templates.filter((template)=>{
        const includedBySearch = !trimmedLowercaseSearch || template.name.toLowerCase().includes(trimmedLowercaseSearch);
        const isMapped = getServiceTemplateIntegratedIds(template).length > 0;
        const includedByMappedStatus = mappedStatusFilter.length === 0 || mappedStatusFilter.includes("mapped") && isMapped || mappedStatusFilter.includes("unmapped") && !isMapped;
        return includedBySearch && includedByMappedStatus;
    });
}
const mappedStatusFilterOptions = [
    {
        value: "mapped",
        label: 'Mapped'
    },
    {
        value: "unmapped",
        label: 'Unmapped'
    }
];
function PMMapServiceRowOld(param) {
    let { template, rowIndex, items, getServiceTemplateIntegratedIds, onMapService } = param;
    const [integratedIds, setIntegratedIds] = useState(()=>getServiceTemplateIntegratedIds(template));
    useDidUpdate(()=>setIntegratedIds(getServiceTemplateIntegratedIds(template)), [
        template
    ]);
    const { PMMapServicesModalTID } = useMemo(()=>GetPMMapServicesModalOldTID(), []);
    const isDeleted = serviceTemplateModel.isDeleted(template);
    return /*#__PURE__*/ _jsx(TableList.Row, {
        size: "fluid",
        className: styles.row,
        items: [
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    ellipsis: true,
                    skin: isDeleted ? 'secondary' : 'standard',
                    children: "".concat(template.name).concat(isDeleted ? ' (Archived)' : '')
                })
            },
            {
                value: /*#__PURE__*/ _jsx(PMMapServiceField, {
                    testId: PMMapServicesModalTID.ServiceRowMapping(rowIndex),
                    options: items,
                    value: integratedIds,
                    onChange: async (ids)=>{
                        setIntegratedIds(ids);
                        onMapService(template, ids);
                    }
                }, template.id)
            }
        ]
    }, template.id);
}
function PMMapServicesTableOld(param) {
    let { pmName, templates: initialTemplates, items, getServiceTemplateIntegratedIds, linkServiceTemplate, refresh } = param;
    const { t: practiceManagementTexts } = useLocaleKeys('integrations.practiceManagement');
    const { t: commonTexts } = useLocaleKeys('common');
    const { columnDefs, headerItems } = useMemo(()=>getTableConfig(pmName), [
        pmName
    ]);
    const [search, setSearch] = useState('');
    const [mappedStatusFilter, setMappedStatusFilter] = useState([]);
    const { showErrorToast } = useOverlayContext();
    const [templates, setTemplates] = useState(initialTemplates);
    const { PMMapServicesModalTID } = useMemo(()=>GetPMMapServicesModalOldTID(), []);
    const enqueueForTemplate = useSerialTaskQueue();
    const filteredTemplates = useMemo(()=>filterTemplates(templates, search, mappedStatusFilter, getServiceTemplateIntegratedIds), [
        templates,
        search,
        mappedStatusFilter,
        getServiceTemplateIntegratedIds
    ]);
    const mapServiceInOrder = (template, itemIds)=>enqueueForTemplate(template.id, ()=>mapService(template, itemIds));
    async function mapService(template, itemIds) {
        const result = await handlePromise(linkServiceTemplate(template.id, itemIds));
        if (!result.success) {
            await refresh();
            showErrorToast("Failed to map service ".concat(template.name), 'map-service-error-toast');
        }
        setTemplates((current)=>current.map((t)=>t.id === template.id ? withIntegratedIds(t, itemIds) : t));
    }
    function withIntegratedIds(template, itemIds) {
        const integrations = template.integrations;
        const setIntegratedIds = {
            [PracticeManagement.Keeper]: ()=>integrations.setKeeperIntegratedIds(itemIds),
            [PracticeManagement.Karbon]: ()=>integrations.setKarbonIntegratedIds(itemIds),
            [PracticeManagement.ClientHub]: ()=>integrations.setClientHubIntegratedIds(itemIds),
            [PracticeManagement.FinancialCents]: ()=>integrations.setFinancialCentsIntegratedIds(itemIds)
        };
        return _object_spread_props(_object_spread({}, template), {
            integrations: setIntegratedIds[pmName]()
        });
    }
    return /*#__PURE__*/ _jsxs(TableList.Provider, {
        columnDefs: columnDefs,
        children: [
            /*#__PURE__*/ _jsxs(PageContentHeader, {
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: cn(styles.filtersRow, styles.filtersRowLegacy),
                        children: [
                            /*#__PURE__*/ _jsx(Searchbox, {
                                bi: {
                                    name: 'pm_services_search'
                                },
                                testId: PMMapServicesModalTID.Search(),
                                placeholder: commonTexts.searchPlaceholder(),
                                value: search,
                                onChange: (e)=>setSearch(e.target.value),
                                onClear: ()=>setSearch('')
                            }),
                            /*#__PURE__*/ _jsx(MultiSelectFilterPicker, {
                                bi: {
                                    name: 'pm_mapped_status_filter'
                                },
                                value: mappedStatusFilter,
                                options: mappedStatusFilterOptions,
                                label: practiceManagementTexts.mappedStatus(),
                                onChange: setMappedStatusFilter,
                                testId: PMMapServicesModalTID.MappedStatusFilter()
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(TableList.Header, {
                        items: headerItems
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                children: filteredTemplates.map((template, idx)=>/*#__PURE__*/ _jsx(PMMapServiceRowOld, {
                        rowIndex: idx,
                        items: items,
                        template: template,
                        getServiceTemplateIntegratedIds: getServiceTemplateIntegratedIds,
                        onMapService: mapServiceInOrder
                    }, template.id))
            })
        ]
    });
}
export default function PMMapServicesModalOld(props) {
    const { t: practiceManagementTexts } = useLocaleKeys('integrations.practiceManagement');
    const { t: errorTexts } = useLocaleKeys('errors');
    const { pmName, dismissModal, getItems } = props;
    const serviceTemplateApi = useServiceTemplateApi();
    const { renderRestfulState } = useRestfulWrapper({
        fetch
    });
    const { PMMapServicesModalTID } = useMemo(()=>GetPMMapServicesModalOldTID(), []);
    async function fetch() {
        const [templates, items] = await Promise.all([
            serviceTemplateApi.getForMappingToPracticeManagementIntegration(),
            getItems()
        ]);
        return {
            templates,
            items
        };
    }
    return /*#__PURE__*/ _jsx(CustomModal, _object_spread_props(_object_spread({
        bi: {
            closeButton: {
                name: 'pm_map_services_modal_close'
            }
        },
        testId: PMMapServicesModalTID.Root()
    }, modalSize.large), {
        onClose: dismissModal,
        children: /*#__PURE__*/ _jsx(Page, {
            height: "100%",
            header: practiceManagementTexts.mapServicesHeader({
                pmName: getPMDetails(pmName).name
            }),
            skin: "secondary",
            children: renderRestfulState({
                renderChild: (param)=>{
                    let { data, refresh } = param;
                    return /*#__PURE__*/ _jsx("div", {
                        "data-testid": PMMapServicesModalTID.Content(),
                        children: data.templates.length > 0 ? /*#__PURE__*/ _jsx(PMMapServicesTableOld, _object_spread_props(_object_spread({}, props, data), {
                            refresh: refresh
                        })) : /*#__PURE__*/ _jsx(PageEmptyState, {
                            testId: PMMapServicesModalTID.NoServices(),
                            illustration: /*#__PURE__*/ _jsx(NoDataIcon, {}),
                            message: practiceManagementTexts.mapServicesEmptyMessage({
                                pmName: getPMDetails(pmName).name
                            }),
                            actions: /*#__PURE__*/ _jsx(CreateProposalButton, {
                                text: practiceManagementTexts.createAgreement()
                            })
                        })
                    });
                },
                renderLoader: ()=>/*#__PURE__*/ _jsx(LoadingIndicator, {}),
                renderError: (param)=>{
                    let { refresh } = param;
                    return /*#__PURE__*/ _jsx(PageErrorState, {
                        testId: PMMapServicesModalTID.FetchError(),
                        message: errorTexts.integration.dataRetrieval(),
                        onRefresh: refresh,
                        skin: "secondary"
                    });
                }
            })
        })
    }));
}
